/* eslint-disable no-prototype-builtins */
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { Link } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ReplayIcon from '@mui/icons-material/Replay'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'

import MediaInstance from './MediaInstance'
import CropNewMediaCarousel from './CropNewMediaCarousel'

import useWindowDimensions from '../../../helpers/windowDimensions'
import { standardButton, addImageWithTextButton, exitModalIconButton } from '../../../helpers/buttons'
import { standardSpinner } from '../../../helpers/spinners'
import { getPayload, getTokenFromLocalStorage, userIsAuthenticated, getEmailFromLocalStorage } from '../../../helpers/storage'
import { mediaSortByArray, positionChangeWidthSm, positionChangeWidthMd, swipelyticsPink, swipelyticsTurquoise, navbarHeight, footerHeight, dashboardNavbarHeight, mediaGuidelinesHTML, maximumCroppedMediaSize, maximumMediaSize } from '../../../helpers/variableDefaults'
import { selectSortCategory } from '../../../helpers/selects'
import { couldNotLoadPageError } from '../../../helpers/errors'
import { switchWithTitle } from '../../../helpers/switches'
import { modifyInDatabase } from '../../../helpers/modifyInDatabase'
import { seoPageTags, customAnalyticsEvent } from '../../../helpers/analytics'
import { searchTextField } from '../../../helpers/textfields'
import { standardPagination } from '../../../helpers/pagination'
import { reloadView, numberWithCommas } from '../../../helpers/globalHelpers'
import { standardInfoTooltip } from '../../../helpers/tooltips'
import { returnSquareImage, returnSquareVideoThumbnail, uploadAWSFilesArray } from '../../../helpers/imageHandling'
import { dummyBox, dummyBoxWidth } from '../../../helpers/globalHelpers'
import { advisoryTypography, saveSuccessfulTypography } from '../../../helpers/typographies'
import { standardErrorContainer } from '../../../helpers/errors'
import { handleImageSelectNewMedia, handleImageSelectNewMediaPrepareForCrop } from '../../../helpers/commonMethods'
import { displayNewMediaJSX, saveAndCancelButtonsJSX } from '../../../helpers/commonJSX'

// Media Page
const Media = (props) => {

  const { isDemo, demoUser } = props

  // Window Dimensions
  const { height, width } = useWindowDimensions()
  
  // Navigate
  const navigate = useNavigate()

  // Params
  const { userId } = useParams()

  // Payload
  const payload = getPayload()

  // User
  const [user, setUser] = useState({})

  // Pagination
  const [activePage, setActivePage] = useState(1)
  const itemsPerPage = 24
  const pageRangeDisplayed = 3

  // New Media
  const [mediaArray, setMediaArray] = useState([]) // All User Media in DB
  const [newMediaArray, setNewMediaArray] = useState([]) // New Media to Upload
  const [mediaUploadSuccessful, setMediaUploadSuccessful] = useState(false)
  const [showCropCarousel, setShowCropCarousel] = useState(false)

  // Loading
  const [loading, setLoading] = useState(false)
  const [loadingNewMedia, setLoadingNewMedia] = useState(false)

  // Errors
  const [errors, setErrors] = useState(false)
  const [newMediaErrors, setNewMediaErrors] = useState(false)
  const [uploadMediaErrors, setUploadMediaErrors] = useState(false)

  // UseEffect — Send to user their own dashboard if payload doesn't match userId
  useEffect(() => {

    if (isDemo) {
      setUser({ ...demoUser })
      setMediaArray(demoUser.media)
    } else if (!userIsAuthenticated()) {
      navigate('/login')
    } else if (userId !== payload.sub) {
      navigate(`/dashboard/${payload.sub}`, { state: { previousLocationPathname: window.location.pathname } })
      // reloadView()
    } else {
      const getData = async () => {
        setLoading(true)
        try {
          const { data: retrievedUser } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/users/${payload.sub}`, {
            headers: {
              Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            },
          })
          // console.log('retrieved User ->', retrievedUser)

          if (payload.sub === retrievedUser._id) {
            // Set States
            setUser(retrievedUser)
            setMediaArray(retrievedUser.hasOwnProperty('media') && retrievedUser.media.length > 0 ? retrievedUser.media : [])
            
            setErrors(false)
            setLoading(false)
          } else {
            navigate(`/`)
            setErrors(false)
            setLoading(false)
          }

        } catch (error) {
          // console.log(error)

          navigate(`/`)

          setErrors(true)
          setLoading(false)
        }
      }

      getData()
    }

  }, [])

  useEffect(() => {
    // console.log('useEffect Runs')
    // console.log('filtersObj ->', filtersObj)
    // console.log('mediaUploadSuccessful ->', mediaUploadSuccessful)

  }, [mediaUploadSuccessful, newMediaErrors, showCropCarousel])

  useEffect(() => {
    // Scroll to top of page when pagination changes
    window.scrollTo(0, 0)

  }, [activePage])

  const resetErrorsAndLoading = () => {
    setNewMediaErrors(false)
    setUploadMediaErrors(false)

    setLoadingNewMedia(false)

    setMediaUploadSuccessful(false)
  }

  // Upload Media Handler 
  const handleImageSelect = async (e) => {
    // console.log('handleUploadedMedia runs')
    // console.log('e.target.files.length ->', e.target.files[0]
    resetErrorsAndLoading()

    // Create an Array for the Uploaded Raw Files
    await handleImageSelectNewMediaPrepareForCrop(e, newMediaArray, setNewMediaArray, setShowCropCarousel, setLoadingNewMedia, setNewMediaErrors, mediaArray.length)

  }


  const mediaInActivePage = (mediaIndexPlusOne) => {
    return (activePage * itemsPerPage >= mediaIndexPlusOne) 
      && (mediaIndexPlusOne >= (((activePage - 1) * itemsPerPage) + 1))
  }


  return (
    <>
      {/* Helmet — for analytics, seo, and page title changing */}
      {seoPageTags(
        'My Media'
      )}

      {/* Body */}
      {loading ?
        <Box sx={{ 
          width: '100%',
          // minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight} - ${dashboardNavbarHeight})`,
          minHeight: `calc(100vh - ${navbarHeight} - ${dashboardNavbarHeight})`,
          mt: 0, mb: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
        }}>
          {standardSpinner()}
        </Box>
        :
        errors ? 
          <Box
            sx={{
              // backgroundColor: 'yellow',
              width: '100%',
              minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight} - ${dashboardNavbarHeight})`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
            }}
          >
            {couldNotLoadPageError()}
          </Box>
          :
          showCropCarousel > 0 ?
            <CropNewMediaCarousel isDemo={isDemo} fromComponent={'dashboardMediaTab'} newMediaArray={newMediaArray} setNewMediaArray={setNewMediaArray} mediaArray={mediaArray} setMediaArray={setMediaArray} user={user} showCropCarousel={showCropCarousel} setShowCropCarousel={setShowCropCarousel} width={width} />
            :
            <Box
              sx={{
                // backgroundColor: 'green',
                pt: 5, pb: 5,
                width: '100%',
                minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight} - ${dashboardNavbarHeight})`,
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
              }}
            > 
              {/* Inactive Subscription */}
              {user.hasOwnProperty('subscriptionIsActive') && !user.subscriptionIsActive &&
                <Box
                  sx={{
                    backgroundColor: 'red',
                    color: 'white',
                    py: 3, px: 3,
                    width: '100%', 
                    // height: '100px',
                    fontSize: '18px', fontWeight: 'bold',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                  }}
                >
                  {`Click 'Manage Subscription' in the 'Settings' tab to reactivate subscription`}
                </Box>
              }

              {/* Button */}
              {newMediaArray.length < 1 &&
                <Box
                  sx={{
                    mb: 3,
                    width: '100%',
                    display: 'flex', flexDirection: width < positionChangeWidthSm ? 'column' : 'column', justifyContent: 'center', alignItems: 'center',
                  }}
                >
                  
                  {user.hasOwnProperty('email') && 
                    addImageWithTextButton(
                      'Upload Media', 
                      `itemImageId`, 
                      `imageURL`, 
                      'contained',
                      'primary',
                      mediaArray.length >= 50,
                      handleImageSelect,
                      '50px',
                      '150px'
                    )
                  }

                  {/* Error Message */}
                  <Box
                    sx={{
                      width: '90%', maxWidth: '450px',
                    }}
                  >
                    {newMediaErrors &&
                      standardErrorContainer(`A selected file is larger than ${Math.round((maximumMediaSize / 1000000) * .8)}MB. Read the Media Guidelines, check your Internet connection, and try again.`)
                    }
                  </Box>

                  {/* Save Successful */}
                  {mediaUploadSuccessful &&
                    saveSuccessfulTypography()
                  }

                  {/* Guidelines */}
                  <Box
                    sx={{
                      mt: 2,
                      width: '90%',
                      display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        // fontSize: '18px',
                        fontWeight: 'bold',
                      }}
                    >
                      MEDIA GUIDELINES
                    </Typography>
                    {standardInfoTooltip(
                      mediaGuidelinesHTML,
                      1
                    )}
                  </Box>
                </Box>
              }

              {/* Media */}
              {!user.hasOwnProperty('media') || user.media.length === 0 ?
                newMediaArray.length === 0 &&
                  <Typography 
                    sx={{ 
                      width: '95%', maxWidth: '600px',
                      color: 'black', mt: 3, fontWeight: 'bold', 
                    }}
                  >
                    No media has been added yet. Click the &apos;Upload Media&apos; button above to get started.
                  </Typography>
                :
                <Box sx={{ mt: 0, mb: 2, width: '100vw', maxWidth: '600px', minWidth: '250px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
                  
                  {/* The Media Items that meet the criteria are sorted and mapped */}
                  {user.hasOwnProperty('media') && mediaArray.length > 0 && 
                    <Box
                      sx={{
                        py: 2,
                        backgroundColor: width < positionChangeWidthSm ? '' : 'whitesmoke',
                        boxShadow: width < positionChangeWidthSm ? 0 : 3, borderRadius: width < positionChangeWidthSm ? 0 : '5px', 
                        width: '100%',
                        maxWidth: '600px',
                        display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* <Typography 
                        textAlign={'center'}
                        sx={{ 
                          mt: 0, mb: 1,
                          width: '100%', maxWidth: '600px',
                          color: 'black', 
                          // color: 'blue',
                          // fontWeight: 'bold', 
                          fontStyle: 'italic',
                        }}
                      >
                        *Add captions to media by clicking on the edit icon
                      </Typography> */}
                      {mediaArray.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).map((mediaItem, mediaItemIndex) => (
                        
                        <Box 
                          key={mediaItemIndex}
                          sx={{
                            // backgroundColor: 'yellow',
                            width: width < positionChangeWidthSm ? '100%' : '33%',
                            mb: 0,
                            display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: (width < positionChangeWidthSm || mediaItemIndex % 3 === 1 || mediaArray.length === 1) ? 'center' : mediaItemIndex % 3 === 0 ? 'flex-end' : 'flex-start',
                          }}
                        >
                          {/* Media */}
                          {
                            mediaInActivePage(mediaItemIndex + 1) &&
                            <MediaInstance mediaItem={mediaItem} mediaItemIndex={mediaItemIndex} user={user} fromComponent={'dashboard'} loadingNewMedia={loadingNewMedia} newMediaArray={newMediaArray} isDemo={isDemo} />
                          }
                        </Box>
                      ))}

                      {/* Add Dummy Boxes if necessary */}
                      {/* {console.log('remainder ->', mediaArray.length % 3)} */}
                      {width >= positionChangeWidthSm 
                      && mediaArray.length > 3 
                      && mediaArray.length % 3 > 0 
                      &&
                        (mediaArray.length % 3 === 1 ? [1, 2] : [1]).map( dummy => (
                          dummyBoxWidth('33%', 0, dummy)
                        ))
                      }
                    </Box>
                  }

                  {/* Message if no media items */}
                  {mediaArray.length === 0 &&
                    <Typography 
                      sx={{ 
                        color: 'black', mt: 4, fontWeight: 'bold', 
                      }}
                    >
                      {`No media has been created. To do so press the 'Upload Media' button above.`}
                    </Typography>
                  }

                  {/* Pagination */}
                  {mediaArray.length > itemsPerPage &&
                    standardPagination(activePage, setActivePage, itemsPerPage, pageRangeDisplayed, mediaArray.length, user.subscriptionIsActive, width)
                  }

                </Box>
              } 
            </Box>
      }
    </>
  )
}

export default Media